import { getAllowance } from '../data/data';

import type { Edition, Meter, Product, ProductKey } from './types';

// (product, edition) -> entitlement data file SKU. Only combinations that have a
// data file are listed; anything omitted resolves to 0.
const DATA_SKU: Record<ProductKey, Partial<Record<Edition, string>>> = {
	jira: {
		free: 'jira-software-free',
		standard: 'jira-software-standard',
		premium: 'jira-software-premium',
		enterprise: 'jira-software-enterprise-sku',
	},
	confluence: {
		free: 'confluence-free',
		standard: 'confluence-standard',
		premium: 'confluence-premium',
		enterprise: 'confluence-enterprise-sku',
	},
	jsm: {
		free: 'jira-servicedesk-free',
		standard: 'jira-servicedesk-standard',
		premium: 'jira-servicedesk-premium',
		enterprise: 'jira-servicedesk-enterprise-sku',
	},
	jpd: {
		free: 'jira-servicedesk-free',
		standard: 'jira-product-discovery-standard',
		premium: 'jira-product-discovery-premium-sku',
		enterprise: 'jira-product-discovery-enterprise',
	},
	teamworkCollection: {
		free: 'teamwork-collection-free-sku',
		standard: 'teamwork-collection-standard-sku',
		premium: 'teamwork-collection-premium-sku',
		enterprise: 'teamwork-collection-enterprise-sku',
	},
	serviceCollection: {
		free: 'service-collection-free',
		standard: 'service-collection-standard',
		premium: 'service-collection-premium',
		enterprise: 'service-collection-enterprise',
	},
};

// Meter -> entitlement usageKey.
const METER_USAGE_KEY: Record<Meter, string> = {
	rovoCredits: 'ai-credit',
	automations: 'automation-credits',
	csResolutions: 'cs-resolution', // no entitlement data yet
	assetsObjects: 'assets-objects',
};

// Meters whose entitlement amount is granted per site rather than per user, so
// the allowance must not be multiplied by the product's unit count.
const PER_SITE_METERS: ReadonlySet<Meter> = new Set<Meter>(['assetsObjects']);

export interface IncludedAllowanceArgs {
	products: Product[];
	meter: Meter;
}

export function calculateIncludedAllowance(args: IncludedAllowanceArgs): number {
	const { products, meter } = args;
	const isPerSite = PER_SITE_METERS.has(meter);
	let sum = 0;
	for (const product of products) {
		const sku = DATA_SKU[product.productKey][product.inputs.edition];
		if (!sku) {
			continue;
		}
		const allowance = getAllowance({
			sku,
			meter: METER_USAGE_KEY[meter],
			billingCycle: product.inputs.billingCycle,
		});
		if (!allowance) {
			continue;
		}
		sum += isPerSite ? allowance : allowance * product.inputs.unitCount;
	}
	return Math.round(sum);
}
